(() => {
  if (customElements.get('vzy-purchase-panel')) return;

  customElements.define('vzy-purchase-panel', class VzyPurchasePanel extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.timers = new Set();
      this.klarnaTimers = new Set();
      this.klarnaGeneration = 0;
      this.variants = this.readVariants();
      this.variantMap = new Map(this.variants.map((variant) => [String(variant.id), variant]));
      this.resolveRoots();
      if (!this.root || !this.form) {
        this.scheduleRetry(80);
        return;
      }
      this.bind();
      this.refresh();
      [100, 350, 800].forEach((delay) => this.scheduleRetry(delay));
    }

    disconnectedCallback() {
      this.klarnaGeneration += 1;
      this.abortController?.abort();
      this.variantObserver?.disconnect();
      this.klarnaObserver?.disconnect();
      this.schemeObserver?.disconnect();
      this.klarnaRootObserver?.disconnect();
      this.klarnaMessageObserver?.disconnect();
      this.klarnaMessageObserver = null;
      this.klarnaMessageRoot = null;
      this.klarnaRatePlacement = null;
      window.cancelAnimationFrame(this.frame);
      this.timers?.forEach((timer) => window.clearTimeout(timer));
      this.timers?.clear();
      this.klarnaTimers?.clear();
      this.abortController = null;
      this.variantObserver = null;
      this.klarnaObserver = null;
      this.schemeObserver = null;
      this.klarnaRootObserver = null;
      this.klarnaAppNotification = null;
      this.klarnaRefreshNotification = null;
      this.klarnaWrapper = null;
      this.currentProductState = null;
      this.productStateOwner = null;
      this.observedVariantInput = null;
      this.lastKlarnaAmount = null;
      this.bound = false;
    }

    resolveRoots() {
      this.root = document.getElementById(this.dataset.vzyMainId);
      this.form = document.getElementById(this.dataset.vzyFormId);
    }

    readVariants() {
      try {
        return JSON.parse(this.querySelector('[data-vzy-purchase-variants]')?.textContent || '[]');
      } catch (error) {
        return [];
      }
    }

    scheduleRetry(delay) {
      const timer = window.setTimeout(() => {
        this.timers.delete(timer);
        if (!this.isConnected) return;
        this.resolveRoots();
        if (!this.root || !this.form) return;
        if (!this.bound) this.bind();
        this.refresh();
      }, delay);
      this.timers.add(timer);
    }

    bind() {
      if (this.bound || !this.root || !this.form) return;
      this.bound = true;
      const signal = this.abortController.signal;
      this.darkPreference = window.matchMedia('(prefers-color-scheme: dark)');
      this.darkPreference.addEventListener('change', () => this.syncKlarnaTheme(), { signal });
      this.schemeObserver = new MutationObserver(() => this.syncKlarnaTheme());
      this.schemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-scheme'] });
      this.klarnaRootObserver = new MutationObserver((records) => {
        if (records.some((record) => record.type === 'attributes' && record.target === this.root)) {
          this.root.querySelectorAll('[data-vzy-klarna-summary]').forEach((summary) => this.syncKlarnaLabels(summary));
        }
        const purchaseNodes = 'vizzyo-quantity-breaks, form[data-type="add-to-cart-form"], input[name="id"], input[name="quantity"], [data-vzy-purchase-price], [data-vzy-smart-main-total]';
        const purchaseChanged = records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) => (
          node.nodeType === 1 && (node.matches?.(purchaseNodes) || node.querySelector?.(purchaseNodes))
        )));
        if (purchaseChanged) this.scheduleSync();
        const inserted = records.some((record) => [...record.addedNodes].some((node) => (
          node.nodeType === 1 && (node.matches?.('klarna-placement') || node.querySelector?.('klarna-placement'))
        )));
        if (!inserted) return;
        this.discoverStructure();
        if (Number.isFinite(this.lastKlarnaAmount)) this.applyKlarnaAmount(this.lastKlarnaAmount);
      });
      this.klarnaRootObserver.observe(this.root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-vzy-klarna-styling', 'data-vzy-klarna-heading', 'data-vzy-klarna-units']
      });
      this.root.addEventListener('input', (event) => this.scheduleSync(event), { signal });
      this.root.addEventListener('change', (event) => this.scheduleSync(event), { signal });
      this.root.addEventListener('vzy:product-state', (event) => {
        window.cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.sync(event);
      }, { signal });

      this.observeVariantInput();
    }

    observeVariantInput() {
      const variantInput = this.variantInput();
      if (variantInput === this.observedVariantInput) return;
      this.variantObserver?.disconnect();
      this.observedVariantInput = variantInput;
      if (variantInput) {
        this.variantObserver = new MutationObserver(() => this.scheduleSync());
        this.variantObserver.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    formInput(name) {
      const control = this.form?.elements?.namedItem?.(name);
      if (control?.matches?.('input') && control.form === this.form) return control;
      return Array.from(this.root?.querySelectorAll(`input[name="${name}"]`) || [])
        .find((input) => input.form === this.form) || null;
    }

    variantInput() { return this.formInput('id'); }
    quantityInput() { return this.formInput('quantity'); }

    quantityController() {
      return Array.from(this.root?.querySelectorAll('vizzyo-quantity-breaks') || []).find((controller) => (
        // Shopify's input[name="id"] shadows HTMLFormElement.id in browsers.
        controller.dataset.formId === this.form?.getAttribute('id')
        && controller.dataset.sectionId === this.dataset.vzySectionId
        && controller.dataset.productId === this.dataset.vzyProductId
        && (!controller.form || controller.form === this.form)
      )) || null;
    }

    readProductState(controller, variant, quantity, event) {
      const valid = (state) => Boolean(controller && state
        && String(state.variantId) === String(variant.id)
        && String(state.sectionId) === this.dataset.vzySectionId
        && String(state.productId) === this.dataset.vzyProductId
        && Number.isInteger(state.quantity) && state.quantity === quantity
        && Number.isInteger(state.unitPriceCents) && state.unitPriceCents >= 0
        && Number.isInteger(state.totalCents) && state.totalCents >= 0
        && typeof state.available === 'boolean');
      const incoming = event?.type === 'vzy:product-state'
        && (event.target === controller || event.target === this.root) ? event.detail : null;
      let state = valid(incoming) ? incoming
        : this.productStateOwner === controller && valid(this.currentProductState) ? this.currentProductState : null;

      // A late-connected panel can miss the initial event. Read the existing
      // controller's calculation, without duplicating its commercial rules.
      if (!state && controller?.quantityInput === this.quantityInput()
        && String(controller.currentVariant?.()?.id) === String(variant.id)
        && this.quantityInput()?.validity.valid
        && typeof controller.packagePrices === 'function' && typeof controller.discountFor === 'function') {
        const discount = controller.discountFor(quantity);
        const prices = controller.packagePrices(Number(variant.price || 0), quantity, discount);
        const snapshot = {
          sectionId: controller.dataset.sectionId,
          productId: Number(controller.dataset.productId),
          variantId: Number(variant.id), quantity,
          unitPriceCents: prices.unit, totalCents: prices.total,
          discountPercent: discount, available: variant.available !== false
        };
        if (valid(snapshot)) state = snapshot;
      }
      this.currentProductState = state;
      this.productStateOwner = state ? controller : null;
      return state;
    }

    discoverStructure() {
      if (!this.root) return;
      const adapter = window.VzyDigital17Adapter;
      if (!adapter?.resolvePurchase) return;
      this.root.setAttribute('data-vzy-purchase-root', '');
      const nodes = adapter.resolvePurchase(this.root, this);
      const { benefitsWrapper, price, installment, packageWrapper, promoWrapper, actions, klarnaPlacement: klarna, klarnaWrapper } = nodes;
      benefitsWrapper?.setAttribute('data-vzy-purchase-benefits', '');

      const priceBadgeSource = this.querySelector(':scope > [data-vzy-price-badges]');
      let priceBadges = price?.querySelector(':scope > [data-vzy-price-badges]') || null;
      if (price && priceBadgeSource && !priceBadges) {
        priceBadges = priceBadgeSource.cloneNode(true);
        priceBadges.hidden = false;
        priceBadges.removeAttribute('data-vzy-price-badges-source');
        priceBadges.setAttribute('data-vzy-price-badges-rendered', '');
        price.append(priceBadges);
      }

      price?.setAttribute('data-vzy-purchase-price', '');
      installment?.setAttribute('data-vzy-purchase-installment', '');
      if (installment && !installment.textContent.trim()) installment.hidden = true;
      packageWrapper?.setAttribute('data-vzy-purchase-packages', '');
      promoWrapper?.setAttribute('data-vzy-purchase-promo', '');
      actions?.setAttribute('data-vzy-purchase-actions', '');
      klarnaWrapper?.setAttribute('data-vzy-purchase-klarna', '');

      if (klarnaWrapper && klarna) {
        this.ensureKlarnaSummary(klarnaWrapper, klarna);
        if (this.klarnaWrapper !== klarnaWrapper) this.observeKlarna(klarnaWrapper);
      }
    }

    ensureKlarnaSummary(wrapper, placement) {
      this.syncKlarnaTheme(placement);
      let summary = wrapper.querySelector(':scope > [data-vzy-klarna-summary]');
      if (!summary) {
        summary = document.createElement('div');
        summary.className = 'vzy-klarna-summary';
        summary.setAttribute('data-vzy-klarna-summary', '');
      }
      if (summary.dataset.vzyKlarnaOpen !== '1') {
        // The rate opens Klarna's own "Află mai multe" dialog, like its link below.
        summary.dataset.vzyKlarnaOpen = '1';
        summary.setAttribute('role', 'button');
        summary.setAttribute('tabindex', '0');
        summary.setAttribute('aria-haspopup', 'dialog');
        const openKlarna = (event) => {
          if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
          const cta = summary.parentElement?.querySelector('klarna-placement')?.shadowRoot?.querySelector('[part~="osm-cta"]');
          if (!cta) return;
          event.preventDefault();
          cta.click();
        };
        summary.addEventListener('click', openKlarna);
        summary.addEventListener('keydown', openKlarna);
      }
      if (summary.dataset.vzyKlarnaVersion !== '6') {
        summary.hidden = true;
        summary.dataset.vzyKlarnaVersion = '6';
        summary.removeAttribute('aria-label');
        summary.innerHTML = `
          <span class="vzy-klarna-summary__heading">3 rate fără dobândă</span>
          <span class="vzy-klarna-summary__rate" aria-hidden="true">
            <span class="vzy-klarna-summary__spoken visually-hidden"></span>
            <span class="vzy-klarna-summary__number" aria-hidden="true"><span data-vzy-klarna-whole></span><sup data-vzy-klarna-fraction></sup></span>
            <span class="vzy-klarna-summary__units" aria-hidden="true">lei / lună</span>
          </span>
        `;
      }
      this.syncKlarnaLabels(summary);
      if (this.lastKlarnaAmount !== 0) placement.removeAttribute('aria-hidden');
      if (summary.parentElement !== wrapper || summary.nextElementSibling !== placement) wrapper.insertBefore(summary, placement);
      this.observeKlarnaMessage(placement);
    }

    syncKlarnaLabels(summary) {
      const customized = this.root?.hasAttribute('data-vzy-klarna-styling');
      const labels = [
        ['.vzy-klarna-summary__heading', 'vzyKlarnaHeading', '3 rate fără dobândă'],
        ['.vzy-klarna-summary__units', 'vzyKlarnaUnits', 'lei / lună']
      ];
      labels.forEach(([selector, setting, fallback]) => {
        const node = summary.querySelector(selector);
        const text = customized && this.root.dataset[setting]?.trim() || fallback;
        if (node && node.textContent !== text) node.textContent = text;
      });
    }

    observeKlarnaMessage(placement) {
      const attach = () => {
        if (!this.isConnected || !this.root?.contains(placement)) return;
        const shadow = placement.shadowRoot;
        if (!shadow) return;
        if (this.klarnaMessageRoot !== shadow) {
          this.klarnaMessageObserver?.disconnect();
          this.klarnaMessageRoot = shadow;
          this.klarnaRatePlacement = placement;
          this.klarnaMessageObserver = new MutationObserver(() => this.syncKlarnaRate(placement));
          this.klarnaMessageObserver.observe(shadow, { childList: true, subtree: true, characterData: true });
        }
        this.syncKlarnaRate(placement);
      };
      if (placement.shadowRoot) attach();
      else if (this.klarnaRatePlacement !== placement) {
        this.klarnaRatePlacement = placement;
        customElements.whenDefined('klarna-placement').then(attach);
      }
    }

    syncKlarnaRate(placement = this.klarnaRatePlacement) {
      if (!placement || !this.root?.contains(placement)) return;
      const wrapper = placement.closest('[data-vzy-purchase-klarna]');
      const summary = wrapper?.querySelector('[data-vzy-klarna-summary]');
      if (!summary) return;
      const text = placement.shadowRoot?.querySelector('[part="osm-message"]')?.textContent.replace(/\s+/g, ' ').trim() || '';
      const match = text.match(/^3 plăți de ([\d. ]+),(\d{2})\s*lei fără dobândă cu Klarna(?:\s|$)/i);
      const total = Number(placement.getAttribute('data-purchase-amount'));
      const expected = Number(this.lastKlarnaAmount ?? total);
      const cents = match ? Number(match[1].replace(/[. ]/g, '')) * 100 + Number(match[2]) : 0;
      const ready = Boolean(match && total === expected && total > 0 && Math.abs(cents - total / 3) < 1);
      if (ready) {
        const whole = match[1].replace(/ /g, '.');
        const setText = (selector, value) => { const node = summary.querySelector(selector); if (node.textContent !== value) node.textContent = value; };
        setText('[data-vzy-klarna-whole]', whole);
        setText('[data-vzy-klarna-fraction]', match[2]);   // eMAG-style, no comma (owner 2026-11-02)
        setText('.vzy-klarna-summary__spoken', `${whole},${match[2]} lei pe lună, fără dobândă.`);
        summary.setAttribute('aria-label', `Plătește în 3 rate egale de ${whole},${match[2]} lei pe lună, fără dobândă. Află mai multe despre Klarna.`);
      }
      wrapper.toggleAttribute('data-vzy-klarna-rate-ready', ready);
      placement.toggleAttribute('data-vzy-klarna-rate-ready', ready);
      summary.hidden = !ready;
    }

    syncKlarnaTheme(placement) {
      const scheme = document.documentElement.dataset.scheme;
      const dark = scheme === 'dark' || ((!scheme || scheme === 'auto') && this.darkPreference?.matches);
      const theme = dark ? 'dark' : 'default';
      const placements = placement ? [placement] : this.root?.querySelectorAll('klarna-placement') || [];
      placements.forEach((item) => {
        // Klarna owns the amount, message and explanation dialog; only select its native theme.
        if (item.getAttribute('data-theme') !== theme) item.setAttribute('data-theme', theme);
      });
    }

    observeKlarna(wrapper) {
      this.klarnaObserver?.disconnect();
      this.klarnaWrapper = wrapper;
      this.klarnaObserver = new MutationObserver((records) => {
        const placementAdded = records.some((record) => [...record.addedNodes].some((node) => (
          node.nodeType === 1
          && (node.matches?.('klarna-placement') || node.querySelector?.('klarna-placement'))
        )));
        const amountChanged = records.some((record) => (
          record.type === 'attributes'
          && record.attributeName === 'data-purchase-amount'
          && record.target.matches?.('klarna-placement')
        ));
        const themeChanged = records.some((record) => record.type === 'attributes' && record.attributeName === 'data-theme');
        if (!placementAdded && !amountChanged && !themeChanged) return;
        const placement = wrapper.querySelector('klarna-placement');
        if (!placement) return;
        this.syncKlarnaTheme(placement);
        if (placementAdded) this.ensureKlarnaSummary(wrapper, placement);
        this.syncKlarnaRate(placement);
        if (Number.isFinite(this.lastKlarnaAmount)) this.applyKlarnaAmount(this.lastKlarnaAmount);
      });
      this.klarnaObserver.observe(wrapper, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-purchase-amount', 'data-theme']
      });
    }

    syncNativeStepper() {
      const input = this.quantityInput();
      const wrapper = input?.closest('quantity-input');
      if (!input || !wrapper) return;
      const quantity = Number(input.value || input.min || input.dataset.min || 1);
      const minimum = Number(input.min || input.dataset.min || 1);
      const maximum = Number(input.max || input.dataset.max || 0);
      const states = [
        [wrapper.querySelector('button[name="minus"]'), quantity <= minimum],
        [wrapper.querySelector('button[name="plus"]'), maximum > 0 && quantity >= maximum]
      ];
      states.forEach(([button, disabled]) => {
        if (!button) return;
        button.disabled = disabled;
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        button.classList.toggle('disabled', disabled);
      });
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const locale = this.dataset.vzyLocale || 'ro-RO';
      const currency = this.dataset.vzyCurrency || 'RON';
      try {
        if (currency === 'RON') return `${new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} lei`;
        return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2).replace('.', ',')} lei`;
      }
    }

    syncOfferBadges(variant) {
      if (!variant) return;
      const price = Number(variant.price || 0);
      const compareAt = Number(variant.compare_at_price || 0);
      const discount = compareAt > price && compareAt > 0 ? Math.round(((compareAt - price) / compareAt) * 100) : 0;
      const scope = this.root || this;
      scope.querySelectorAll('[data-vzy-sale-badge]').forEach((badge) => { badge.hidden = discount <= 0; });
      const discountValue = scope.querySelector('[data-vzy-discount-value]');
      if (discountValue) discountValue.textContent = `${discount}%`;
    }

    syncShippingBadge(total, available) {
      const label = this.querySelector('[data-vzy-shipping-label]');
      const conditionalLabel = this.dataset.vzyShippingConditionalLabel;
      if (!label || !conditionalLabel) return;
      const threshold = Number(this.dataset.vzyShippingThreshold);
      const qualifies = available && Number.isFinite(total) && Number.isFinite(threshold)
        && threshold > 0 && total >= threshold;
      const text = qualifies ? this.dataset.vzyShippingQualifiedLabel : conditionalLabel;
      if (text && label.textContent !== text) label.textContent = text;
    }

    applyKlarnaAmount(amount) {
      if (!Number.isFinite(amount) || amount < 0 || !this.root) return;
      const value = String(Math.round(amount));
      this.root.dataset.vzyPurchaseAmount = value;
      let amountChanged = false;
      this.root.querySelectorAll('klarna-placement').forEach((placement) => {
        if (placement.getAttribute('data-purchase-amount') !== value) {
          amountChanged = true;
          placement.setAttribute('data-purchase-amount', value);
        }
        if (amount === 0) {
          if (!placement.hidden) placement.setAttribute('data-vzy-klarna-suspended', '');
          placement.hidden = true;
          placement.setAttribute('aria-hidden', 'true');
        } else {
          if (placement.hasAttribute('data-vzy-klarna-suspended')) {
            placement.hidden = false;
            placement.removeAttribute('data-vzy-klarna-suspended');
          }
          placement.removeAttribute('aria-hidden');
        }
      });
      this.syncKlarnaRate();
      // Retry late SDKs or real amount drift without repeatedly rebuilding a stable widget.
      const notify = (owner, method, key, args) => {
        const fn = owner?.[method];
        if (typeof fn !== 'function') return;
        const previous = this[key];
        if (!amountChanged && previous?.amount === value && previous.fn === fn) return;
        try {
          fn.apply(owner, args);
          this[key] = { amount: value, fn };
        } catch (error) {}
      };
      notify(window.KOSMApp, 'updatePurchaseAmount', 'klarnaAppNotification', [Number(value)]);
      notify(window.Klarna?.OnsiteMessaging, 'refresh', 'klarnaRefreshNotification', []);
    }

    syncKlarna(amount) {
      const nextAmount = Number.isFinite(amount) && amount > 0 ? Math.round(amount) : 0;
      if (nextAmount === this.lastKlarnaAmount) {
        this.applyKlarnaAmount(nextAmount);
        return;
      }
      this.lastKlarnaAmount = nextAmount;
      this.klarnaGeneration += 1;
      const generation = this.klarnaGeneration;
      this.klarnaTimers.forEach((timer) => {
        window.clearTimeout(timer);
        this.timers.delete(timer);
      });
      this.klarnaTimers.clear();

      if (nextAmount === 0) {
        this.applyKlarnaAmount(0);
        return;
      }

      [0, 500, 3000, 6000].forEach((delay) => {
        if (delay === 0) {
          this.applyKlarnaAmount(this.lastKlarnaAmount);
          return;
        }
        const timer = window.setTimeout(() => {
          this.klarnaTimers.delete(timer);
          this.timers.delete(timer);
          if (this.isConnected && generation === this.klarnaGeneration) this.applyKlarnaAmount(this.lastKlarnaAmount);
        }, delay);
        this.klarnaTimers.add(timer);
        this.timers.add(timer);
      });
    }

    syncMainTotal(state, variant) {
      if (!this.root?.classList.contains('vzy-smart-product')) return;
      const wrapper = this.root.querySelector('[data-vzy-purchase-price]');
      const summary = wrapper?.querySelector('[data-vzy-smart-main-total]');
      if (!summary) return;
      const valid = state && state.available !== false && this.quantityController()
        && String(state.variantId) === String(this.variantInput()?.value)
        && state.quantity === Number(this.quantityInput()?.value);
      const selector = this.root.querySelector('vizzyo-quantity-breaks');
      // BF2026 (owner 2026-10-24): the BF price per camera is the main price, also for one camera.
      const bf = Boolean(valid && selector?.bfTiers && state.discountPercent > 0);
      const show = Boolean(valid && state.totalCents > 0 && (state.quantity > 1 || bf));
      summary.hidden = !show;
      wrapper.classList.toggle('vzy-smart-multi', show && !bf);
      wrapper.classList.toggle('vzy-smart-bf', show && bf);
      const bfLine = summary.querySelector('[data-vzy-smart-main-bf]');
      if (bfLine) bfLine.hidden = !(show && bf);
      if (!show) return;
      const money = (cents) => selector?.formatMoney(cents) || new Intl.NumberFormat(this.dataset.vzyLocale || 'ro-RO', {style:'currency',currency:this.dataset.vzyCurrency || 'RON'}).format(cents / 100);
      const countLabel = state.quantity === 1 ? '1 cameră'
        : `${state.quantity}${state.quantity % 100 === 0 || state.quantity % 100 >= 20 ? ' de' : ''} camere`;
      summary.querySelector('[data-vzy-smart-main-count]').textContent = ' · ' + countLabel;
      summary.querySelector('[data-vzy-smart-main-amount]').textContent = money(state.totalCents);
      summary.querySelector('[data-vzy-smart-main-unit]').textContent = money(state.unitPriceCents);
      // eMAG-style (owner 2026-11-02): the old price (PRP) above the price; snippets/vzy-sup-money-js adds the superscript.
      const was = summary.querySelector('[data-vzy-smart-main-was]');
      const unitCompare = Number(variant?.compare_at_price || 0);
      if (was) {
        was.hidden = !(bf && unitCompare > Number(variant?.price || 0));
        if (!was.hidden) was.innerHTML = `PRP: <s>${money(unitCompare)}</s>`;
      }
      const label = summary.querySelector('[data-vzy-smart-main-label]');
      if (label) label.hidden = bf && state.quantity === 1;
      if (!bf || !bfLine) return;
      const regular = Number(variant?.price || 0) * state.quantity;
      const code = String(selector.bfCode || 'BF2026').replace(/[&<>"']/g, '');
      bfLine.innerHTML = `<b class="vzy-smart-price-total__bf-code">${code} &minus;${state.discountPercent}%</b>`
        + `<span>aplicat automat în coș · fără cod <s>${money(regular)}</s></span>`;
    }

    scheduleSync(event) {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => this.sync(event));
    }

    sync(event) {
      this.resolveRoots();
      this.observeVariantInput();
      this.discoverStructure();
      this.syncNativeStepper();
      const variantId = String(this.variantInput()?.value || '');
      const variant = this.variantMap.get(variantId)
        || window.VzyDigital17Adapter?.currentVariant?.(this.root, variantId)
        || null;
      if (!variant) {
        this.dataset.vzyVariantState = 'unresolved';
        (this.root || this).querySelectorAll('[data-vzy-sale-badge]').forEach((badge) => { badge.hidden = true; });
        this.currentProductState = null;
        this.productStateOwner = null;
        this.syncMainTotal(null, null);
        this.syncShippingBadge(0, false);
        this.syncKlarna(0);
        return;
      }
      this.dataset.vzyVariantState = 'ready';
      this.syncOfferBadges(variant);
      const pendingController = this.quantityController();
      if (pendingController?.localName === 'vizzyo-quantity-breaks' && !customElements.get('vizzyo-quantity-breaks')) {
        // Its script loads after this one: keep the server-rendered first paint (BF price), sync once it is defined.
        customElements.whenDefined('vizzyo-quantity-breaks').then(() => this.scheduleSync());
        return;
      }
      const quantity = Math.max(1, Math.round(Number(this.quantityInput()?.value || 1)));
      const controller = this.quantityController();
      const state = this.readProductState(controller, variant, quantity, event);
      const total = state ? state.totalCents : controller ? 0 : Number(variant.price || 0) * quantity;
      this.syncMainTotal(state, variant);
      this.syncShippingBadge(total, variant.available !== false && state?.available !== false
        && this.quantityInput()?.validity.valid !== false);
      this.syncKlarna(total);
    }

    refresh() {
      this.discoverStructure();
      this.sync();
    }
  });
})();

